import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './components/Header';
import ContractTable from './components/ContractTable';
import SearchBar from './components/SearchBar';
import FilterSidebar from './components/FilterSidebar';
import PDFViewer from './components/PDFViewer';
import { fetchContracts } from './services/api';

function App() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 25
  });
  const [filters, setFilters] = useState({
    search: '',
    workType: '',
    publicationName: '',
    publicationDate: '',
    rightsLevel: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [selectedContract, setSelectedContract] = useState(null);
  const [showPDF, setShowPDF] = useState(false);
  const [viewerMode, setViewerMode] = useState('contract');

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchContracts({ ...filters, page: 1 });
        setContracts(response.contracts);
        setPagination(response.pagination);
        setHasLoaded(true);
      } catch (error) {
        console.error('Error loading contracts:', error);
      }
    };

    loadData();
  }, [filters]); // Only filters

  const loadContracts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetchContracts({ ...filters, page });
      setContracts(response.contracts);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    console.log('[DEBUG] handleFilterChange called with:', newFilters);
    setFilters(prevFilters => {
      const newState = { ...prevFilters, ...newFilters };
      console.log('[DEBUG] New filter state:', newState);
      return newState;
    });
  }, []);

  const handlePageChange = useCallback((page) => {
    loadContracts(page);
  }, []);

  const handleViewPDF = useCallback((contract) => {
    setSelectedContract(contract);
    setViewerMode('contract');
    setShowPDF(true);
  }, []);

  const handleViewSource = useCallback((contract) => {
    setSelectedContract(contract);
    setViewerMode('source');
    setShowPDF(true);
  }, []);

  const handleSearchChange = useCallback((search) => {
    handleFilterChange({ search });
  }, [handleFilterChange]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Persistent Header */}
      <Header />

      {/* Main Content Area */}
      <div className="pt-4">
        {showPDF && selectedContract ? (
          /* PDF Viewer Full Screen with Header Visible */
          <div className="fixed inset-0 pt-20 bg-white z-40">
            <PDFViewer
              contract={selectedContract}
              mode={viewerMode}
              onClose={() => setShowPDF(false)}
            />
          </div>
        ) : (
          /* Main App Content */
          <div className="pl-4 pr-4 sm:pr-6 lg:pr-8 py-4">
            <div className="flex gap-8">
              {/* Sidebar */}
              <div className="w-64 flex-shrink-0">
                <FilterSidebar filters={filters} onFilterChange={handleFilterChange} />
              </div>

              {/* Main Content */}
              <div className="flex-1">
                {/* Search Bar */}
                <div className="mb-6">
                  <SearchBar
                    search={filters.search}
                    onSearchChange={handleSearchChange}
                    loading={loading}
                  />
                </div>

                {/* Table */}
                <div className="bg-white shadow rounded-lg">
                  {!hasLoaded ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                      <span className="ml-3 text-gray-600">Loading contracts...</span>
                    </div>
                  ) : (
                    <ContractTable
                      contracts={contracts}
                      loading={loading}
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      onViewPDF={handleViewPDF}
                      onViewSource={handleViewSource}
                      filters={filters}
                      onFilterChange={handleFilterChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;